import {
  Placement,
  UseFloatingOptions,
  arrow,
  flip,
  limitShift,
  offset,
  shift,
  computePosition,
  autoUpdate,
} from "@floating-ui/react";
import {
  ComponentPropsWithoutRef,
  useEffect,
  useId,
  useRef,
  useState,
  useMemo,
  useLayoutEffect,
  useCallback,
} from "react";
import { TooltipContent } from "./TooltipContent";
import { TooltipTrigger } from "./TooltipTrigger";
import { supportsPopover } from "../../internal/functions/supportsPopover";
import { TooltipLegacy } from "../TooltipLegacy/TooltipLegacy";
import { TooltipContext, TooltipContextProps } from "./TooltipContext";
import {
  safeHidePopover,
  safeShowPopover,
} from "../../internal/functions/safePopover";

/**
 * Props for the Tooltip component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type TooltipProps = {
  /**
   * The content to be wrapped by the tooltip
   */
  children?: ComponentPropsWithoutRef<"div">["children"];
  /**
   * Unique identifier for the tooltip
   */
  id?: ComponentPropsWithoutRef<"div">["id"];
  /**
   * Disables the shift of floating element
   * @internal
   */
  disableShift?: boolean;
  /**
   * Disables the flip of floating element
   * @internal
   */
  disableFlip?: boolean;
  /**
   * Disables the flip cross-axis fallback of floating element
   * @internal
   */
  disableFlipFallback?: boolean;
  /**
   * Changes interaction to use hover
   */
  openOnHover?: boolean;
  /**
   * Changes height and positioning for select/combobox
   * @default top
   */
  placement?: Placement;
  /**
   * Controls the open state of the tooltip
   */
  open?: boolean;
  /**
   * Default open state of the tooltip
   * @default false
   */
  defaultOpen?: boolean;
  /**
   * Callback when open state changes
   */
  onOpenChange?: UseFloatingOptions["onOpenChange"];
  /**
   * Delay before showing the tooltip
   * @default 100
   */
  delay?: number;
  /**
   * Offset distance from the trigger element
   * @default 5
   */
  offset?: number;
  /**
   * Fallback placement options
   */
  fallbackPlacements?: Placement[];
  /**
   * Root element for the tooltip
   * @deprecated to be removed on next major
   * This is not needed anymore as the Popover API is used.
   * Popover API uses top-layer for its popover content.
   */
  root?: HTMLElement | React.MutableRefObject<HTMLElement | null> | null;
};

export const TooltipElement = (props: TooltipProps) => {
  const {
    children,
    delay = 100,
    placement = "top",
    open,
    defaultOpen = false,
    disableShift,
    disableFlip,
    disableFlipFallback,
    id,
    offset: offsetProp = 5,
    fallbackPlacements,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    root,
  } = props;
  const uid = useId();
  const [invoker, setInvoker] =
    useState<TooltipContextProps["invoker"]>(undefined);
  const [tooltip, setTooltip] =
    useState<TooltipContextProps["invoker"]>(undefined);
  const [rootNode, setRootNode] = useState<TooltipContextProps["rootNode"]>();
  const [arrowElement, setArrowElement] =
    useState<TooltipContextProps["arrowElement"]>(undefined);
  const [openState, setOpenState] = useState<TooltipContextProps["openState"]>(
    open ?? defaultOpen,
  );
  const arrowRef = useRef<HTMLSpanElement>(null);
  const [tooltipId, setTooltipId] = useState<TooltipContextProps["tooltipId"]>(
    id ?? `tooltip-${uid.replace(/:/g, "")}`,
  );

  // TODO: Find a way to test these properly!
  // Stryker disable all : Currently relying on Chromatic test for all visuals
  /* istanbul ignore next */
  const updatePosition = useCallback(() => {
    if (!invoker || !tooltip) return;
    // disable position update when closing
    if (!openState) return;
    computePosition(invoker, tooltip, {
      placement: placement,
      middleware: [
        offset(offsetProp),
        flip({
          mainAxis: !disableFlip,
          crossAxis: false,
          fallbackAxisSideDirection: disableFlipFallback ? "none" : "end",
          fallbackPlacements,
          padding: 5,
        }),
        shift({
          padding: 5,
          mainAxis: !disableShift,
          crossAxis: false,
          limiter: limitShift({
            mainAxis: !disableShift,
          }),
        }),
        arrow({
          element: arrowElement ? arrowElement : null,
          padding: 5,
        }),
      ],
    }).then(({ x, y, placement, middlewareData }) => {
      const side = placement.split("-")[0];

      const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right",
      }[side];

      const arrowDeg = () => {
        if (staticSide === "top") return 180;
        if (staticSide === "bottom") return 0;
        if (staticSide === "left") return 90;
        return 270;
      };

      Object.assign(tooltip.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
      if (middlewareData.arrow && arrowElement) {
        const { x, y } = middlewareData.arrow;
        Object.assign(arrowElement.style, {
          left: x != null ? `${x}px` : "",
          top: y != null ? `${y}px` : "",
          right: "",
          bottom: "",
          [staticSide as string]: `${-arrowElement.offsetWidth + 2}px`,
          transform: `rotate(${arrowDeg()}deg)`,
        });
      }
    });
  }, [
    invoker,
    tooltip,
    openState,
    placement,
    offsetProp,
    disableFlip,
    disableFlipFallback,
    fallbackPlacements,
    disableShift,
    arrowElement,
  ]);

  const resetKey = useRef(0);
  // Effect to add autoUpdate to eventListener
  // while the popover is opened
  useLayoutEffect(() => {
    if (!tooltip || !invoker) return;
    if (!openState) {
      resetKey.current += 1;
      return;
    }

    let requestID: number | null = null;
    const cleanup = autoUpdate(invoker, tooltip, () => {
      requestID = requestAnimationFrame(updatePosition);
    });

    return () => {
      if (requestID) cancelAnimationFrame(requestID);
      cleanup();
    };
  }, [invoker, tooltip, updatePosition, openState]);
  // Stryker restore all

  const isControlled = typeof open !== "undefined";
  const openTimeoutRef = useRef<number | NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<number | NodeJS.Timeout | null>(null);

  // Main function to open tooltip
  const openTooltip = useCallback(() => {
    clearTimeout(closeTimeoutRef.current as number | undefined);
    clearTimeout(openTimeoutRef.current as number | undefined);
    if (!tooltip || !rootNode?.querySelector(`#${tooltipId}`)) return;
    openTimeoutRef.current = setTimeout(() => {
      safeShowPopover(tooltip);
      setOpenState(true);
    }, delay);
  }, [tooltip, rootNode, tooltipId, delay]);

  // Main function to close tooltip
  const closeTooltip = useCallback(
    (fn?: () => void) => {
      clearTimeout(openTimeoutRef.current as number | undefined);
      clearTimeout(closeTimeoutRef.current as number | undefined);
      if (!tooltip || !openState) return;
      closeTimeoutRef.current = setTimeout(() => {
        fn?.();
        setOpenState(false);
        safeHidePopover(tooltip);
      }, 100);
    },
    [openState, tooltip],
  );

  // Updating openness for controlled
  const initialRender = useRef(true);
  useEffect(() => {
    if (initialRender.current === true) {
      if (openState) openTooltip();
      initialRender.current = false;
      return;
    }
  }, [openTooltip, openState, isControlled]);

  // Updating openness for controlled
  useEffect(() => {
    if (!isControlled) return;
    if (open) {
      openTooltip();
    } else {
      closeTooltip();
    }
  }, [closeTooltip, isControlled, open, openTooltip]);

  const value = useMemo(() => {
    updatePosition();
    return {
      tooltipId,
      setTooltipId,
      invoker,
      setInvoker,
      tooltip,
      setTooltip,
      arrowElement,
      setArrowElement,
      openTooltip,
      closeTooltip,
      placement,
      openState,
      setOpenState,
      disableFlipFallback,
      controlled: isControlled,
      arrowRef,
      resetKey: resetKey.current,
      rootNode,
      setRootNode,
    };
  }, [
    updatePosition,
    tooltipId,
    setTooltipId,
    invoker,
    tooltip,
    arrowElement,
    openTooltip,
    closeTooltip,
    placement,
    openState,
    disableFlipFallback,
    isControlled,
    rootNode,
  ]);

  if (!supportsPopover()) return <TooltipLegacy {...props} />;

  return (
    <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>
  );
};

/**
 * Tooltip component for displaying contextual information on hover or focus.
 *
 * Features:
 * - Displays contextual information when hovering over or focusing on elements
 * - Supports multiple placement options (top, bottom, left, right)
 * - Automatic positioning with fallback placements
 * - Accessible with proper ARIA roles and keyboard navigation
 * - Supports controlled and uncontrolled open states
 * - Configurable delay and offset settings
 * - Uses modern Popover API with fallback to legacy implementation
 * - Automatic arrow positioning and rotation
 * - Real-time position updates during scroll and resize
 *
 * @example
 * <Tooltip placement="top" delay={200}>
 *   <Tooltip.Trigger>
 *     <Button>Hover me</Button>
 *   </Tooltip.Trigger>
 *   <Tooltip.Content>This is helpful information</Tooltip.Content>
 * </Tooltip>
 */
export const Tooltip = Object.assign(TooltipElement, {
  /**
   * TooltipTrigger component that wraps the element that triggers the tooltip.
   *
   * Features:
   * - Wraps any React element to make it a tooltip trigger
   * - Handles mouse and keyboard interactions
   * - Provides proper ARIA attributes for accessibility
   * - Supports all standard HTML element props
   * - Automatically clones child elements with proper refs
   * - Handles both focusable and non-focusable children
   * - Manages mouse enter/leave events for hover behavior
   *
   * @example
   * <Tooltip.Trigger>
   *   <Button>Click me</Button>
   * </Tooltip.Trigger>
   */
  Trigger: TooltipTrigger,
  /**
   * TooltipContent component that displays the tooltip content.
   *
   * Features:
   * - Displays the tooltip content with proper positioning
   * - Includes an arrow pointing to the trigger element
   * - Supports keyboard navigation and escape key to close
   * - Handles mouse interactions for hover behavior
   * - Accessible with proper ARIA roles
   * - Supports all standard div element props
   * - Automatic focus management for keyboard navigation
   * - Real-time position updates
   * - Handles both string and complex content
   *
   * @example
   * <Tooltip.Content>
   *   This tooltip provides helpful context
   * </Tooltip.Content>
   */
  Content: TooltipContent,
  Context: TooltipContext,
});
